<template>
  <div class="w-full space-y-6">
    <CrudBasic :tabla="tabla">
      <template #details-tabla="{  row }">
        <div>
          <div class="mb-2">
            <span
              class="flex items-center gap-1.5 text-xs font-semibold text-gray-800 dark:text-white"
            >
              <svg-icon
                class="h-4 w-4 text-blue-500"
                type="mdi"
                :path="mdiHospitalBoxOutline"
              ></svg-icon>
              {{
                row.ciadTipoAdmision === 'CONSULTA'
                  ? 'Consultas médicas'
                  : 'Servicios'
              }}
            </span>
          </div>
          <div class="space-y-2">
            <div
              v-for="tech in row.technologias as ICitaTechnologiasSalud[]"
              :key="tech.cittId"
              class="bg-white dark:bg-gray-700 p-2 rounded-lg shadow-sm border border-gray-100 dark:border-gray-600"
            >
              <div class="flex justify-between items-center">
                <div>
                  <p class="text-xs font-medium text-gray-900 dark:text-white">
                    {{
                      tech.contratoxservicio?.servicio?.coseNombre ||
                      tech.cittTechnologia
                    }}
                  </p>
                  <p class="text-[10px] text-gray-500 dark:text-gray-400">
                    {{ formatearCupsServicio(tech.contratoxservicio) }}
                  </p>
                </div>
                <div class="justify-center items-center flex gap-2">
                  <span
                    class="inline-flex items-center rounded-md px-2 py-0.5 text-[10px] font-medium"
                    :class="getStatusClass(tech.cittEstado)"
                  >
                    {{ tech.cittEstado }}
                  </span>

                <TooltipDefault label="Generar PDF">
                  <PrintButtonDefault
                    v-if="tech.cittEstado === 'TRAMITADO' && tech.cittId" :id="tech.cittId" :tipo-impresion="tipoImpresion" :is-preview="true" :mini="true" :include-graficas="tech.contratoxservicio?.servicio?.coseIsGraficas === 1" />
                </TooltipDefault>

                <TooltipDefault label="Historia clinica">
                  <ButtonDefault
                    v-if="tech.cittEstado === 'TRAMITADO' && tech.cittId" type-style="outline"
                    size="xs" color="blue" class="ml-2" @click="handleOpenVistaPreview(tech)">
                    <MdiIcon icono="mdiClipboardTextClockOutline" />
                  </ButtonDefault>
                </TooltipDefault>

                <VerAnexosButton
                  v-if="tech.cittEstado === 'TRAMITADO CON ANEXO' && tech.cittId"
                  class="ml-2"
                  :citt-id="tech.cittId!"
                  :anexos-relaciones="tech.anexosRelaciones"
                />

                <TooltipDefault label="Ver admisión">
                  <ButtonDefault
                    v-if="canViewAdmision(row)" type-style="outline"
                    size="xs" color="green" class="ml-2" @click="handleOpenViewAdmisionModal(row)">
                    <EyeIcon class="w-4 h-4" />
                  </ButtonDefault>
                </TooltipDefault>

                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </CrudBasic>
  </div>
</template>

<script setup lang="ts">
import { formatearCupsServicio } from '@/modules/Contratacion/utils/cupsServicio'
import { computed, h, ref, type Ref } from 'vue'
import 'v3-infinite-loading/lib/style.css'
import { EyeIcon } from '@heroicons/vue/24/outline'
import {
  filtrar,
  validatePermiso,
} from '@/modules/Core/usings/generales'
import type { ICitaAdmision } from '@/modules/Admision/interfaces'
import { admisionService } from '@/services'
import ColumnStatusTable from '@/modules/Core/components/general/ColumnStatusTable.vue'
import type { ICitaTechnologiasSalud } from '@/modules/Asistencial/interfaces'
import { tipoImpresionAsistencialDefault, type itemListEstados, type listEstados } from '@/modules/Core/usings/constantes'
import PrintButtonDefault from '@/modules/Asistencial/components/PrintButtonDefault.vue'
import { useAdmisionTechComponents } from '@/modules/Asistencial/composables'
import { generateDocMedico } from '@/modules/Core/usings/impresion'
import type { IFormStructure } from '@/modules/Configuracion/interfaces/IFormComponent'
import type { IDataTable, IFiltersOptions } from '@/modules/Core/interfaces'
import MdiIcon from '@/modules/Core/components/general/MdiIcon.vue'
import type { IConfPersonas } from '@/modules/Administracion/interfaces'
import type { IConfMaestra } from '@/modules/Configuracion/interfaces'
import type { IConfCups, IConfServicios } from '@/modules/Contratacion/interfaces'

import { mdiHospitalBoxOutline } from '@mdi/js'
import ColumnProfesionalTable from '@/modules/Core/components/general/ColumnProfesionalTable.vue'
import { useModal } from '@/modules/Core/composables/useModal'
import ModalAdmisionView from './ModalAdmisionView.vue'
import VerAnexosButton from '@/modules/Asistencial/components/VerAnexosButton.vue'
// import ModalConsultationView from './ModalConsultationView.vue'
const { openModal } = useModal()
const { extractTechComponents } = useAdmisionTechComponents()

const { coppId, tipo, excludeFilters } = defineProps({
  coppId: { type: Number, default: null },
  tipo: { type: String, default: '' },
  excludeFilters: { type: Array<string>, default: [] }
})

const FILTER_BY_TIPO: Record<string, Partial<IFiltersOptions>> = {
  PROCEDIMIENTO: {
    name: 'cocuId',
    label: 'Procedimiento',
    urlSearch: 'cupService',
    action: 'searchByNameCod',
    params: { cocuTipo: 'PROCEDIMIENTO' }
  },
  VACUNA: {
    name: 'cocuId',
    label: 'Vacuna',
    urlSearch: 'cupService',
    action: 'searchByNameCod',
    params: { cocuTipo: 'PROCEDIMIENTO', isVacuna: 1 }
  },
  LABORATORIO: {
    name: 'cocuId',
    label: 'Laboratorio',
    urlSearch: 'cupService',
    action: 'searchByNameCod',
    params: { cocuTipo: 'LABORATORIO' }
  },
  DEFAULT: {
    name: 'coseId',
    label: 'Servicio',
    urlSearch: 'servicioService',
    action: 'searchByname',
    params: {}
  }
}

const citas: Ref<ICitaAdmision[]> = ref([] as ICitaAdmision[])
const tipoImpresion = computed(() => {
  if (tipo === 'PROCEDIMIENTO') {
    return tipoImpresionAsistencialDefault.PROCEDIMIENTOS
  } else if (tipo === 'LABORATORIO') {
    return tipoImpresionAsistencialDefault.LABORATORIOS
  } else if (tipo === 'CONSULTA') {
    return tipoImpresionAsistencialDefault.HISTORIA
  }
  return tipoImpresionAsistencialDefault.HISTORIA
})

const printConsolidado = async (admision: ICitaAdmision) => {
  try {
    window.notificacion.loading().circle()
    const techs = admision.technologias?.filter(t => t.cittEstado === 'TRAMITADO') || []
    if (techs.length === 0) {
      window.notificacion.loading().remove()
      return
    }

    const components = await extractTechComponents(admision, ['TRAMITADO'])
    if (components.length === 0) {
      window.notificacion.loading().remove()
      return
    }

    const formData: IFormStructure = {
      technologia: techs[0],
      admision,
      components
    }

    await generateDocMedico(formData, tipoImpresion.value)
    window.notificacion.loading().remove()
  } catch (error) {
    console.error('Error al generar PDF consolidado:', error)
    window.notificacion.loading().remove()
  }
}

const load = async () => {
  const params = filtrar(
    tabla.value.filtros,
    tabla.value.pag,
    tabla.value.limit
  )
  const { data } = await admisionService.search({
    ...params,
    coppId: coppId,
    ciadTipoAdmision: tipo,
    ciadTipoEstado: 'FINALIZADO'
  })
  citas.value = data.body.rows
  return data.body
}
const tabla = ref({
  noTitle: true,
  title: '',
  style: 'table',
  identification: 'tabla-citas-paciente',
  search: load,
  isList: true,
  columns: [
    {
      name: 'coprId',
      class: 'lg:col-span-1 md:col-span-2 sm:col-span-2 ',
      label: 'Profesional',
      expandable: true,
      component: (item: ICitaAdmision) => {
        const principal =
          item.technologias && item.technologias?.length > 0
            ? item.technologias?.filter(
                (e: ICitaTechnologiasSalud) => e.cittAdmisionado
              )[0]
            : null
        return h(ColumnProfesionalTable, { item: principal?.empleado, technologies: item.technologias, fecha: new Date(item.ciadFechaOrigen as string)})
      }
    },
    {
      name: 'ciadTipoAdmision',
      label: 'Detalle',
      component: (item: ICitaAdmision) => {
        const datos = [
          { name: item.contrato?.cocoNombre || 'Sin contrato', tipo: 'contrato', fullWidth: true },
          { name: item.contrato?.empresa?.coemNombre || 'Sin empresa', tipo: 'empresa', fullWidth: true },
          { name: item.ciadTipoAdmision, tipo: 'tipoAtencion' },
          { name: item.ciadTipoEstado, tipo: 'estadoAsistencial' },
        ]
        const paramColumnStatusTable: listEstados = { items: datos as itemListEstados[], dispensario: false }
        if (tipo == 'DISPENSARIO'){
          paramColumnStatusTable.dispensario = true
        }
        return h(ColumnStatusTable, paramColumnStatusTable)
      }
    },
    {
      name: 'Action',
      label: '',
      width: '60px',
      btns: [
        {
          label: 'PDF consolidado',
          click: (item: ICitaAdmision) => {
            printConsolidado(item)
          },
          show: (item: ICitaAdmision) => {
            if (tipo !== 'LABORATORIO') return false
            const tramitados = item.technologias?.filter(t => t.cittEstado === 'TRAMITADO') || []
            return tramitados.length >= 2
          },
          icon: 'DocumentArrowDownIcon',
          class: 'inline-flex flex-shrink-0 justify-center items-center size-8 rounded-full text-green-500 hover:bg-green-100 hover:text-green-800 focus:z-10 focus:outline-none focus:ring-2 focus:ring-green-500'
        }
      ]
    }
  ],
  rows: [],
  pag: 1,
  count: 0,
  countPag: 0,
  limit: 5,
  loading: false,
  filtros: [
    {
      name: 'coprId',
      label: 'Profesional',
      type: 'select',
      urlSearch: 'personaService',
      action: 'listEmpleado',
      mapOptions: (item: IConfPersonas) => {
        return { optionText: item.copeShortName, optionValue: item.copeId }
      },
      list: []
    },
    {
      name: 'fechaRango',
      label: 'Fecha',
      type: 'date-range',
      value: null
    },
    {
      name: 'cittEstado',
      campo: 'cittEstado',
      label: 'Estado servicio',
      type: 'select',
      mapOptions: (item: IConfMaestra) => {
        return {
          optionText: item.comaNombrelargo,
          optionValue: item.comaNombrecorto
        }
      },
      urlSearch: `maestraService`,
      action: 'searchByType',
      list: [],
      params: 'ESTADO_TECHNOLOGIAS',
    },
    {
      name: 'ciadTipoEstado',
      campo: 'ciadTipoEstado',
      label: 'Estado cita',
      type: 'select',
      value: 'FINALIZADO',
      mapOptions: (item: IConfMaestra) => {
        return {
          optionText: item.comaNombrelargo,
          optionValue: item.comaNombrecorto
        }
      },
      urlSearch: `maestraService`,
      action: 'searchByType',
      list: [],
      params: 'ESTADO_ADMISIONES',
    },
    (() => {
      const cfg = (FILTER_BY_TIPO[tipo] || FILTER_BY_TIPO.DEFAULT) as Partial<IFiltersOptions>
      return {
        name: cfg.name,
        label: cfg.label,
        type: 'search',
        mapOptions: (item: IConfCups | IConfServicios) => {
          if (!item || typeof item !== 'object') return { optionText: '', optionValue: null }
          try {
            if ('coseNombre' in item && 'coseId' in item) {
              return { optionText: (item as IConfServicios).coseNombre, optionValue: (item as IConfServicios).coseId }
            }
            if ('cocuCups' in item && 'cocuNombre' in item && 'cocuId' in item) {
              const ii = item as IConfCups
              return { optionText: `${ii.cocuCups}-${ii.cocuNombre}`, optionValue: ii.cocuId }
            }
          } catch (error) {
            console.error('Error en mapOptions:', error)
          }
          return { optionText: '', optionValue: null }
        },
        urlSearch: cfg.urlSearch,
        action: cfg.action ,
        params: cfg.params,
        minlength: 3,
        list: []
      }
    })(),
  ] as IFiltersOptions[]
} as IDataTable<ICitaAdmision>)
tabla.value.filtros = tabla.value.filtros.filter( e => !excludeFilters.some(a => a == e.name))
const handleOpenViewAdmisionModal = (admision: ICitaAdmision) => {
  openModal(`view_admision_detail`, { component: ModalAdmisionView, props: { admisionSeleccionada: admision }, isClosable: true, onlyOne: false })
}
const handleOpenVistaPreview = (tech: ICitaTechnologiasSalud) => {
  const ruta = `/asistenciales/Consulta/View/${tech.cittId}`
  window.open(ruta, '_blank')
  // openModal(`modal-historia-clinica`, { component: ModalConsultationView, props: { cittId: tech.cittId }, isClosable: true, onlyOne: false })
}
const canViewAdmision = (item: ICitaAdmision) => {
  if(!(validatePermiso(['verConsulta']) && tipo !== 'DISPENSARIO' && tipo !== 'MEDIAPLICADOS' && tipo !== 'INSUAPLICADOS')) return false
  const ciadTipoAdmision = item.ciadTipoAdmision
  const ciadTipoEstado = item.ciadTipoEstado
  const excluidos = ['AGENDADO', 'INASISTIDO', 'CANCELADO']

  if (tipo === 'VACUNA')
    return ciadTipoAdmision === 'PROCEDIMIENTO' && ciadTipoEstado && !excluidos.includes(ciadTipoEstado)

  if (tipo === 'PROCEDIMIENTO')
    return ciadTipoAdmision === 'PROCEDIMIENTO' && ciadTipoEstado === 'FINALIZADO'

  if (ciadTipoAdmision === 'LABORATORIO' || ciadTipoAdmision === 'PROCEDIMIENTO')
    return ciadTipoEstado && !excluidos.includes(ciadTipoEstado)

  return ciadTipoEstado === 'FINALIZADO' || ciadTipoEstado === 'ADMISIONADO'
}

const getStatusClass = (estado: string | undefined) => {
  switch (estado) {
    case 'TRAMITADO':
      return 'bg-green-50 text-green-600 ring-1 ring-inset ring-green-600/10 dark:bg-green-400/10 dark:text-green-400 dark:ring-green-400/20'
    case 'TRAMITADO CON ANEXO':
      return 'bg-blue-50 text-blue-600 ring-1 ring-inset ring-blue-600/10 dark:bg-blue-400/10 dark:text-blue-400 dark:ring-blue-400/20'
    case 'INASISTIDO':
      return 'bg-gray-200 text-gray-800 ring-1 ring-inset ring-gray-800/10 dark:bg-gray-400/10 dark:text-gray-400 dark:ring-gray-400/20'
    case 'ABIERTO':
      return 'bg-amber-50 text-amber-600 ring-1 ring-inset ring-amber-600/10 dark:bg-amber-400/10 dark:text-amber-400 dark:ring-amber-400/20'
    case 'CANCELADO':
      return 'bg-rose-50 text-rose-600 ring-1 ring-inset ring-rose-600/10 dark:bg-rose-400/10 dark:text-rose-400 dark:ring-rose-400/20'
    case 'NO REALIZADO':
      return 'bg-gray-50 text-gray-600 ring-1 ring-inset ring-gray-600/10 dark:bg-gray-400/10 dark:text-gray-400 dark:ring-gray-400/20'
    case 'FINALIZADO':
      return 'bg-green-50 text-green-600 ring-1 ring-inset ring-green-600/10 dark:bg-green-400/10 dark:text-green-400 dark:ring-green-400/20'
    default:
      return 'bg-gray-50 text-gray-600 ring-1 ring-inset ring-gray-600/10 dark:bg-gray-400/10 dark:text-gray-400 dark:ring-gray-400/20'
  }
}
</script>
